/**
 * Unified Appointment Selectors — Single Source of Truth
 *
 * Every appointment view MUST derive its data through these selectors.
 * The canonical dataset is salesOpportunities; enrichment is done once here.
 */

import { isFuture, isToday, isTomorrow, isPast, parseISO } from "date-fns";
import { formatSafe, toDateSafe } from "@/lib/dateUtils";
import type { Opportunity, OpportunityStage } from "./opportunityTypes";
import type { FollowUpTask } from "./opportunityTypes";
import type { Conversation, Message } from "./communicationTypes";
import {
  scoreLeadConversion,
  type LeadTemperature,
} from "./conversionIntelligence";
import {
  getProtectionStatus,
  detectNoShow,
  getRecoveryActions,
  type ProtectionStatus,
  type NoShowDetection,
  type RecoveryAction,
} from "./appointmentProtection";
import type { ConsultationSessionData } from "@/services/consultation.service";

/* ═══════ CANONICAL STATUS ═══════ */

export type AppointmentStatus =
  | "scheduled"
  | "in_progress"
  | "completed"
  | "no_show"
  | "rescheduled"
  | "locked";

export type AppointmentOutcome =
  | "sold"
  | "no_sale"
  | "rescheduled"
  | "canceled"
  | "no_show"
  | "upcoming"
  | "needs_outcome";

/** Derive outcome from an opportunity — single canonical implementation */
export function deriveOutcome(opp: Opportunity): AppointmentOutcome {
  // Once a job exists, this appointment is operationally completed for the appointments queue.
  if (opp.linkedJobId) return "sold";
  if (opp.stage === "Closed Won") return "sold";
  if (opp.disposition) {
    if (opp.disposition.reason === "no_show" || opp.disposition.reason === "not_home") return "no_show";
    if (opp.disposition.reason === "cancelled") return "canceled";
    return "no_sale";
  }
  if (opp.stage === "Closed Lost") return "no_sale";
  if (opp.stage === "Follow-Up Needed") return "no_sale";
  if (opp.stage === "Appointment Scheduled" && opp.appointmentTime) {
    const apptDate = toDateSafe(opp.appointmentTime);
    if (!apptDate) return "upcoming";
    return isFuture(apptDate) ? "upcoming" : "needs_outcome";
  }
  if (opp.stage === "Appointment Completed") return "no_sale";
  return "upcoming";
}

/** Derive command-center access status for a specific rep */
export function deriveAccessStatus(
  lead: Opportunity,
  allLeads: Opportunity[],
  now: Date,
): { status: AppointmentStatus; locked: boolean; unlockLabel?: string } {
  if (lead.stage === "Closed Won" || lead.linkedJobId) return { status: "completed", locked: false };
  if (lead.stage === "Appointment Completed") return { status: "in_progress", locked: false };
  if (lead.disposition) return { status: "completed", locked: false };
  if (!lead.appointmentTime) return { status: "scheduled", locked: false };

  const apptTime = new Date(lead.appointmentTime);

  // First appointment of the day is always accessible
  const todayAppts = allLeads
    .filter(
      (l) =>
        l.appointmentTime &&
        l.assignedRep === lead.assignedRep &&
        !l.linkedJobId &&
        l.stage !== "Closed Won" &&
        !l.disposition,
    )
    .filter((l) => {
      const t = new Date(l.appointmentTime!);
      return t.toDateString() === now.toDateString();
    })
    .sort((a, b) => new Date(a.appointmentTime!).getTime() - new Date(b.appointmentTime!).getTime());

  const isFirstOfDay = todayAppts.length > 0 && todayAppts[0].id === lead.id;
  if (isFirstOfDay) return { status: "scheduled", locked: false };

  if (apptTime > now) {
      return {
        status: "locked",
        locked: true,
        unlockLabel: `Available at ${formatSafe(apptTime, "h:mm a")}`,
      };
  }

  return { status: "scheduled", locked: false };
}

/* ═══════ ENRICHED APPOINTMENT ═══════ */

export interface UnifiedAppointment {
  /** The underlying opportunity record */
  opportunity: Opportunity;
  /** Canonical outcome */
  outcome: AppointmentOutcome;
  /** Access status for command-center use */
  accessStatus: AppointmentStatus;
  locked: boolean;
  unlockLabel?: string;
  /** Enrichment */
  isHighValue: boolean;
  needsFollowUp: boolean;
  hasConversation: boolean;
  hasConsultationSession: boolean;
  temperature: LeadTemperature;
  conversionScore: number;
  protection: ProtectionStatus;
  noShowDetection: NoShowDetection | null;
  recoveryActions: RecoveryAction[];
}

/* ═══════ CORE ENRICHMENT (runs once) ═══════ */

export interface AppointmentSelectorDeps {
  salesOpportunities: Opportunity[];
  conversations: Conversation[];
  messages: Message[];
  followUpTasks: FollowUpTask[];
  consultationSessions?: ConsultationSessionData[];
}

function buildFirstAppointmentIdsByRepDay(opportunities: Opportunity[]): Set<string> {
  const firstByRepDay = new Map<string, Opportunity>();

  for (const opportunity of opportunities) {
    if (
      !opportunity.appointmentTime ||
      !opportunity.assignedRep ||
      opportunity.linkedJobId ||
      opportunity.stage === "Closed Won" ||
      opportunity.disposition
    ) {
      continue;
    }

    const appointmentTime = new Date(opportunity.appointmentTime);
    if (Number.isNaN(appointmentTime.getTime())) continue;

    const key = `${opportunity.assignedRep}|${appointmentTime.toDateString()}`;
    const current = firstByRepDay.get(key);
    if (
      !current?.appointmentTime ||
      appointmentTime.getTime() < new Date(current.appointmentTime).getTime()
    ) {
      firstByRepDay.set(key, opportunity);
    }
  }

  return new Set(Array.from(firstByRepDay.values()).map((opportunity) => opportunity.id));
}

function deriveAccessStatusWithFirstAppointmentIndex(
  lead: Opportunity,
  firstAppointmentIdsByRepDay: Set<string>,
  now: Date,
): { status: AppointmentStatus; locked: boolean; unlockLabel?: string } {
  if (lead.stage === "Closed Won" || lead.linkedJobId) return { status: "completed", locked: false };
  if (lead.stage === "Appointment Completed") return { status: "in_progress", locked: false };
  if (lead.disposition) return { status: "completed", locked: false };
  if (!lead.appointmentTime) return { status: "scheduled", locked: false };

  const apptTime = new Date(lead.appointmentTime);
  if (firstAppointmentIdsByRepDay.has(lead.id)) return { status: "scheduled", locked: false };

  if (apptTime > now) {
    return {
      status: "locked",
      locked: true,
      unlockLabel: `Available at ${formatSafe(apptTime, "h:mm a")}`,
    };
  }

  return { status: "scheduled", locked: false };
}

function setIfPresent<T>(map: Map<string, T>, key: string | null | undefined, value: T) {
  const normalizedKey = String(key ?? "").trim();
  if (normalizedKey && !map.has(normalizedKey)) {
    map.set(normalizedKey, value);
  }
}

/**
 * getAllAppointments — the single enrichment pass.
 * Every other selector filters this output.
 */
export function getAllAppointments(deps: AppointmentSelectorDeps): UnifiedAppointment[] {
  const { salesOpportunities, conversations, messages, followUpTasks, consultationSessions = [] } = deps;
  const now = new Date();
  const firstAppointmentIdsByRepDay = buildFirstAppointmentIdsByRepDay(salesOpportunities);

  const conversationByOpportunityId = new Map<string, Conversation>();
  for (const conversation of conversations) {
    setIfPresent(conversationByOpportunityId, conversation.linkedLeadId, conversation);
    setIfPresent(conversationByOpportunityId, conversation.linkedQuoteId, conversation);
  }

  const messagesByConversationId = new Map<string, Message[]>();
  for (const message of messages) {
    const list = messagesByConversationId.get(message.conversationId) ?? [];
    list.push(message);
    messagesByConversationId.set(message.conversationId, list);
  }

  const activeFollowUpByOpportunityId = new Set(
    followUpTasks
      .filter((task) => task.status !== "completed")
      .map((task) => task.opportunityId),
  );

  const consultationSessionByEntityId = new Map<string, ConsultationSessionData>();
  for (const session of consultationSessions) {
    setIfPresent(consultationSessionByEntityId, session.lead_id, session);
    setIfPresent(consultationSessionByEntityId, session.quote_id, session);
  }

  return salesOpportunities
    // Exclude plain leads with no appointment, but keep progressed records
    // (e.g., Closed Won / Appointment Completed) even if appointmentTime is absent.
    .filter((o) => {
      if (o.appointmentTime) return true;
      return o.stage !== "Lead";
    })
    .map((o): UnifiedAppointment => {
      const outcome = deriveOutcome(o);
      const access = deriveAccessStatusWithFirstAppointmentIndex(o, firstAppointmentIdsByRepDay, now);
      const conversation = conversationByOpportunityId.get(o.id);
      const scopedMessages = conversation ? messagesByConversationId.get(conversation.id) ?? [] : [];
      const scopedConversations = conversation ? [conversation] : [];
      const existingFollowUp = activeFollowUpByOpportunityId.has(o.id);
      const tempScore = scoreLeadConversion(o, scopedConversations, scopedMessages);
      const protection = getProtectionStatus(o, scopedConversations as any, scopedMessages as any);
      const noShowDetection = detectNoShow(o);
      const recoveryActions = noShowDetection?.isNoShow ? getRecoveryActions(o) : [];

      // Determine if there's an active consultation session that has progressed beyond arrival
      const oppQuoteId = typeof o.quoteId === "string" ? o.quoteId : null;
      const matchingSession =
        consultationSessionByEntityId.get(o.id) ??
        consultationSessionByEntityId.get(String(o.leadId ?? "")) ??
        (oppQuoteId ? consultationSessionByEntityId.get(oppQuoteId) : undefined);
      // Only consider it "started" (resumable) if a session exists AND current_step is NOT "arrival"
      const hasConsultationSession = matchingSession ? matchingSession.current_step !== "arrival" : false;

      return {
        opportunity: o,
        outcome,
        accessStatus: access.status,
        locked: access.locked,
        unlockLabel: access.unlockLabel,
        isHighValue: (o.totalEstimate || 0) >= 15000,
        needsFollowUp:
            (outcome === "no_sale" || outcome === "no_show") && !existingFollowUp,
        hasConversation: Boolean(conversation),
        hasConsultationSession,
        temperature: tempScore.temperature,
        conversionScore: tempScore.score,
        protection,
        noShowDetection,
        recoveryActions,
      };
    });
}

/* ═══════ SELECTORS ═══════ */

export function getAppointmentsByRep(
  all: UnifiedAppointment[],
  repName: string,
): UnifiedAppointment[] {
  return all.filter((a) => a.opportunity.assignedRep === repName);
}

export function getTodaysAppointments(
  all: UnifiedAppointment[],
  repName?: string,
): UnifiedAppointment[] {
  return all
    .filter((a) => {
      if (!a.opportunity.appointmentTime) return false;
      if (!isToday(parseISO(a.opportunity.appointmentTime))) return false;
      if (repName && a.opportunity.assignedRep !== repName) return false;
      return true;
    })
    .sort(
      (a, b) =>
        new Date(a.opportunity.appointmentTime!).getTime() -
        new Date(b.opportunity.appointmentTime!).getTime(),
    );
}

export function getAppointmentsNeedingOutcome(
  all: UnifiedAppointment[],
): UnifiedAppointment[] {
  return all.filter((a) => a.outcome === "needs_outcome");
}

export function getRescheduledAppointments(
  all: UnifiedAppointment[],
): UnifiedAppointment[] {
  return all.filter((a) => a.outcome === "rescheduled");
}

export function getUpcomingAppointments(
  all: UnifiedAppointment[],
): UnifiedAppointment[] {
  return all
    .filter((a) => a.outcome === "upcoming" || a.outcome === "needs_outcome")
    .sort((a, b) => {
      if (a.outcome === "needs_outcome" && b.outcome !== "needs_outcome") return -1;
      if (b.outcome === "needs_outcome" && a.outcome !== "needs_outcome") return 1;
      return (
        new Date(a.opportunity.appointmentTime || a.opportunity.createdAt).getTime() -
        new Date(b.opportunity.appointmentTime || b.opportunity.createdAt).getTime()
      );
    });
}

export function getCompletedAppointments(
  all: UnifiedAppointment[],
): UnifiedAppointment[] {
  return all
    .filter((a) => a.outcome !== "upcoming" && a.outcome !== "needs_outcome")
    .sort(
      (a, b) =>
        new Date(b.opportunity.updatedAt).getTime() -
        new Date(a.opportunity.updatedAt).getTime(),
    );
}

/**
 * Command-center scoped view: today's + overdue + actionable for a rep.
 * Excludes completed/closed records already linked to jobs.
 */
export function getCommandCenterAppointments(
  all: UnifiedAppointment[],
  repName?: string | null,
): UnifiedAppointment[] {
  return all
    .filter((a) => !repName || a.opportunity.assignedRep === repName)
    .filter((a) => !a.opportunity.linkedJobId)
    .filter((a) =>
      ["Appointment Scheduled", "Appointment Completed", "Lead"].includes(a.opportunity.stage),
    )
    .filter((a) => !a.opportunity.disposition)
    .sort((a, b) => {
      const order: Record<string, number> = { in_progress: 0, scheduled: 1, locked: 2, completed: 3 };
      return (order[a.accessStatus] ?? 4) - (order[b.accessStatus] ?? 4);
    });
}

/* ═══════ APPOINTMENTS WITHOUT OUTCOME ═══════ */

export interface AppointmentWithoutOutcomeDeps {
  salesOpportunities: Opportunity[];
  appointmentResults: { opportunityId: string }[];
}

/**
 * getAppointmentsWithoutOutcome — canonical selector for appointments
 * that have been completed but have no corresponding result record.
 */
export function getAppointmentsWithoutOutcome(
  deps: AppointmentWithoutOutcomeDeps,
): Opportunity[] {
  const resultOppIds = new Set(deps.appointmentResults.map((r) => r.opportunityId));
  return deps.salesOpportunities.filter((o) => {
    if (o.stage !== "Appointment Completed") return false;
    return !resultOppIds.has(o.id);
  });
}
